import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Mail, Phone, Calendar, User as UserIcon, ShieldCheck,
  CheckCircle2, Clock, XCircle, FileText, Package as PackageIcon,
  MessageSquarePlus, Trash2, Loader2, Download,
} from "lucide-react";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const fmtDT = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso); }
};

const fmtRp = (n) => "Rp " + (parseInt(n || 0, 10) || 0).toLocaleString("id-ID");

const DOC_LABEL = {
  passport: "Paspor",
  id_card: "KTP",
  vaccine_cert: "Sertifikat Vaksin",
  family_card: "Kartu Keluarga",
  marriage_book: "Buku Nikah",
};

export default function JamaahDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/jamaah/${userId}`);
      setData(data);
    } catch (e) {
      toast.error("Gagal memuat detail jamaah");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setPosting(true);
    try {
      await api.post(`/admin/jamaah/${userId}/notes`, { note: note.trim() });
      setNote("");
      toast.success("Catatan ditambahkan");
      load();
    } catch { toast.error("Gagal menambah catatan"); }
    finally { setPosting(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Hapus catatan ini?")) return;
    try {
      await api.delete(`/admin/jamaah/${userId}/notes/${noteId}`);
      toast.success("Catatan dihapus");
      load();
    } catch { toast.error("Gagal menghapus catatan"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }
  if (!data) return null;

  const { user, bookings, payment_timeline, documents, admin_notes } = data;

  return (
    <div className="bg-background min-h-screen" data-testid="jamaah-detail-page">
      <SEO title={`Jamaah — ${user.name}`} description="Detail jamaah admin" />

      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="container-x py-4 flex items-center gap-4">
          <Link
            to="/admin?tab=jamaah"
            data-testid="back-to-admin"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> Kembali
          </Link>
          <div className="h-4 w-px bg-border" />
          <h1 className="font-serif text-xl text-primary flex-1 truncate">{user.name || user.email}</h1>
          {user.is_admin && <span className="text-[10px] uppercase tracking-wide bg-primary text-white rounded-full px-2 py-1">Admin</span>}
          {user.soft_deleted && <span className="text-[10px] uppercase tracking-wide bg-red-500 text-white rounded-full px-2 py-1">Nonaktif</span>}
        </div>
      </header>

      <div className="container-x py-10 grid lg:grid-cols-3 gap-6">
        {/* Profile */}
        <aside className="lg:col-span-1 space-y-6" data-testid="jamaah-profile">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-accent/15 grid place-items-center">
                  <UserIcon className="h-6 w-6 text-accent" />
                </div>
              )}
              <div className="min-w-0">
                <p className="font-serif text-xl text-primary truncate">{user.name || "-"}</p>
                <p className="text-xs text-muted-foreground">{user.user_id}</p>
              </div>
            </div>

            <dl className="mt-6 space-y-3 text-sm">
              <div className="flex gap-2">
                <Mail className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                <dd className="text-primary break-all">{user.email}</dd>
              </div>
              <div className="flex gap-2">
                <Phone className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                <dd className="text-primary">{user.phone || "-"}</dd>
              </div>
              <div className="flex gap-2">
                <Calendar className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                <dd className="text-muted-foreground">Terdaftar {fmtDT(user.created_at)}</dd>
              </div>
              {user.auth_provider && (
                <div className="flex gap-2">
                  <ShieldCheck className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  <dd className="text-muted-foreground capitalize">{user.auth_provider} — {user.email_verified ? "Verified" : "Belum verifikasi"}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Admin notes */}
          <div className="bg-card border border-border rounded-lg p-6" data-testid="jamaah-notes">
            <h3 className="font-serif text-lg text-primary flex items-center gap-2">
              <MessageSquarePlus className="h-4 w-4 text-accent" /> Catatan Admin
            </h3>
            <form onSubmit={addNote} className="mt-4 space-y-2">
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Tambah catatan…"
                rows={3}
                data-testid="jamaah-note-input"
              />
              <Button type="submit" disabled={posting || !note.trim()} className="w-full" data-testid="jamaah-note-submit">
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Catatan"}
              </Button>
            </form>
            <ul className="mt-5 space-y-3">
              {admin_notes.length === 0 && (
                <li className="text-xs text-muted-foreground text-center py-3">Belum ada catatan.</li>
              )}
              {admin_notes.map((n) => (
                <li key={n.id} className="border-l-2 border-accent pl-3 py-1" data-testid={`note-${n.id}`}>
                  <p className="text-sm text-primary">{n.note}</p>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {n.by_name || n.by} • {fmtDT(n.at)}
                    </p>
                    <button onClick={() => deleteNote(n.id)} className="text-muted-foreground hover:text-red-600" data-testid={`note-delete-${n.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main content */}
        <main className="lg:col-span-2 space-y-6">
          {/* Bookings */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="jamaah-bookings">
            <h3 className="font-serif text-xl text-primary flex items-center gap-2">
              <PackageIcon className="h-4 w-4 text-accent" /> Riwayat Booking ({bookings.length})
            </h3>
            {bookings.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground text-center py-8">Belum ada booking.</p>
            ) : (
              <ul className="mt-4 divide-y divide-border">
                {bookings.map((b) => (
                  <li key={b.id} className="py-4" data-testid={`booking-${b.id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <p className="font-medium text-primary">
                          <span className="text-accent">{b.booking_number}</span> — {b.package_name || b.package_id}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{fmtDT(b.created_at)}</span>
                          <span>{b.pax || 1} pax</span>
                          {b.departure_pref && <span>Preferensi: {b.departure_pref}</span>}
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wide rounded-full px-2.5 py-1 whitespace-nowrap
                        ${b.status === "confirmed" ? "bg-green-100 text-green-800"
                          : b.status === "cancelled" ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"}`}>
                        {b.status || "new"}
                      </span>
                    </div>
                    {b.notes && <p className="mt-2 text-xs text-muted-foreground italic">"{b.notes}"</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Payment timeline */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="jamaah-payment-timeline">
            <h3 className="font-serif text-xl text-primary flex items-center gap-2">
              <Clock className="h-4 w-4 text-accent" /> Timeline Pembayaran
            </h3>
            {payment_timeline.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground text-center py-8">Belum ada aktivitas pembayaran.</p>
            ) : (
              <ol className="mt-4 relative border-l-2 border-accent/30 pl-6 space-y-6">
                {payment_timeline.map((e, idx) => (
                  <li key={idx} className="relative" data-testid={`timeline-${idx}`}>
                    <span className="absolute -left-[31px] top-1 w-5 h-5 rounded-full grid place-items-center bg-accent/20">
                      {e.verified === true ? <CheckCircle2 className="h-3 w-3 text-green-600" />
                        : e.verified === false ? <XCircle className="h-3 w-3 text-red-600" />
                        : <Clock className="h-3 w-3 text-yellow-600" />}
                    </span>
                    <p className="text-sm text-primary">
                      <span className="text-accent font-medium">{e.booking_number}</span>
                      {e.amount && <> — {fmtRp(e.amount)}</>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{fmtDT(e.at)}</p>
                    {e.verified_by && (
                      <p className="text-xs text-muted-foreground italic">
                        {e.verified === true ? "Diverifikasi" : "Ditolak"} oleh {e.verified_by} • {fmtDT(e.verified_at)}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </section>

          {/* Documents */}
          <section className="bg-card border border-border rounded-lg p-6" data-testid="jamaah-documents">
            <h3 className="font-serif text-xl text-primary flex items-center gap-2">
              <FileText className="h-4 w-4 text-accent" /> Dokumen Terupload ({documents.length})
            </h3>
            {documents.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground text-center py-8">Belum ada dokumen.</p>
            ) : (
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {documents.map((d, idx) => (
                  <li key={idx} className="border border-border rounded-md p-4 flex items-center gap-3" data-testid={`doc-${idx}`}>
                    <FileText className="h-5 w-5 text-accent shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-primary">{DOC_LABEL[d.kind] || d.kind}</p>
                      <p className="text-xs text-muted-foreground">
                        {d.booking_number} • {fmtDT(d.uploaded_at)}
                      </p>
                    </div>
                    {d.verified === true && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                    {d.verified === false && <XCircle className="h-4 w-4 text-red-600" />}
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/70" data-testid={`doc-link-${idx}`}>
                      <Download className="h-4 w-4" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
